import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useQueryParams from 'hooks/useQueryParams';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  DashboardMetricColumns,
  DashboardMetricContentTransformations,
  DEAL_SORTING_VALUES,
  CUSTOM_COL_SIZES,
  HeadingTranslations,
} from './data';
import { Button, Input } from 'ncoded-component-library';
import SearchIcon from 'icons/Search.icon';
import { FIRST_PAGE, NEW_WINDOW_PROPS } from 'data/global';
import TableIcon from 'icons/Table.icon';
import utils, { checkIsPublished } from 'utils';
import { DashboardMetric } from './types';
import { useNavigate } from 'react-router-dom';
import useCallbackRef from 'hooks/useCallbackRef';
import PaymentInvoiceSkipped from './components/PaymentInvoiceSkipped';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import FilterMetrics from './components/FilterMetrics';
import { MetricsFilters } from './components/FilterMetrics/FilterMetrics.component';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';

import './ListingMetrics.styles.scss';

const prepareFiltersForRequest = (object: MetricsFilters) => {
  const newObj: Partial<MetricsFilters> = {};

  Object.entries(object).forEach(
    ([key, value]) => (newObj[key as keyof MetricsFilters] = !value),
  );

  return newObj;
};

const RELATIONS = '["listing","dealRoom","user"]';

type ListingMetricsProps = {
  className?: string;
};

const ListingMetrics: React.FC<ListingMetricsProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();
  const { popServerError } = useContext(PopNotificationsContext);

  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    setMultipleQueryParams,
    removeAllQueryParams,
    params: { searchString, sort, page },
  } = useQueryParams<{ searchString: string; page: string; sort: string }>();
  const [inputSearchValue, setInputSearchValue] = useState(searchString || '');
  const [dealMetrics, setDealMetrics] = useState<DashboardMetric[]>([]);

  const [beFiltersApplied, setBeFiltersApplied] =
    useState<MetricsFilters | null>(null);

  const [brokersRec, setBrokersRec] = useState<string[]>([]);
  const [listingsRec, setListingsRec] = useState<string[]>([]);

  const [dashboardMetric, setDashboardMetric] = useState<DashboardMetric>();

  const [bmResponse, bmResponseRef] = useCallbackRef<ModalRef>();

  const classes = classNames('listing-metrics', className);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const navigate = useNavigate();

  const handleOnRowClick = useCallback(
    ({ listing }: DashboardMetric) => {
      if (!listing) return;

      const { industry, slugName, type, stage } = listing;

      const isPublished = checkIsPublished(stage);

      if (!isPublished || !industry || !slugName || !type) return;

      const redirectionListingLink = utils.composeBusinessUrl(
        industry,
        slugName,
        type,
      );

      navigate(redirectionListingLink);
    },
    [navigate],
  );

  useEffect(() => {
    inputSearchValue
      ? (async () => {
          try {
            const encodedSearch = encodeURIComponent(inputSearchValue);

            const {
              data: { listings, brokers },
            } = await api.portfolio.admin.getDashMetSearchvalues(encodedSearch);
            setListingsRec(listings);
            setBrokersRec(brokers);
          } catch (e) {
            popServerError(e);
          }
        })()
      : (() => {
          setInputSearchValue('');
          setBrokersRec([]);
          setListingsRec([]);
        })();
  }, [inputSearchValue, popServerError]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const {
          data: { items, totalPages },
        } = await api.portfolio.admin.getDashboardMetrics({
          $sort: sort,
          $page: page,
          $limit: limit,
          $relations: RELATIONS,
          $searchString: searchString,
          ...(beFiltersApplied
            ? prepareFiltersForRequest(beFiltersApplied)
            : {}),
        });
        setDealMetrics(items);
        setTotalPages(totalPages);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [beFiltersApplied, page, limit, searchString, sort, popServerError]);

  const onSearchSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault();
      const formData = new FormData(ev.target as HTMLFormElement);
      const searchString = formData.get('search')?.toString() || '';
      removeAllQueryParams();
      setMultipleQueryParams({ searchString, page: FIRST_PAGE });
    },
    [removeAllQueryParams, setMultipleQueryParams],
  );

  const [formFocus, setFormFocus] = useState(false);

  const updateDashboardMetric = useCallback(
    (dashboardMetric: Partial<DashboardMetric>) =>
      setDealMetrics((old) =>
        old.map((el) =>
          el._id === dashboardMetric._id ? { ...el, ...dashboardMetric } : el,
        ),
      ),
    [],
  );

  const openInvoiceModal = useCallback(
    (metric: DashboardMetric) => {
      setDashboardMetric(metric);
      bmResponse?.open();
    },
    [bmResponse],
  );

  const renderedContent = useMemo(
    () =>
      DashboardMetricColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (dashboardMetric: DashboardMetric) =>
            DashboardMetricContentTransformations[col](
              dashboardMetric,
              openInvoiceModal,
            ),
        }),
        {},
      ),
    [openInvoiceModal],
  );

  const downloadCSV = useCallback(async () => {
    try {
      setIsDownloading(true);

      const {
        data: { url },
      } = await api.portfolio.admin.getDashboardMetricsCsv();

      if (url) {
        utils.downloadUrl({
          href: url,
          ...NEW_WINDOW_PROPS,
        });
      }
    } catch (e) {
      popServerError(e);
    } finally {
      setIsDownloading(false);
    }
  }, [popServerError]);

  useEffect(() => {
    const handleClickedOutside = () => {
      setFormFocus(false);
    };

    window.document.addEventListener('click', handleClickedOutside);

    return () => {
      window.document.removeEventListener('click', handleClickedOutside);
    };
  }, []);

  return (
    <div className={classes}>
      <div className="listing-metrics__heading">
        <h1>{t('listingMetrics')}</h1>

        <div className="listing-metrics__heading__searching-cont">
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />

          <Button
            className="export-button"
            icon={TableIcon}
            disabled={isDownloading}
            onClick={downloadCSV}
          >
            {t('logCsv')}
          </Button>

          <FilterMetrics setMetricsFilters={setBeFiltersApplied} />

          <form
            onSubmit={onSearchSubmit}
            onClick={(e) => {
              e.stopPropagation();
              setFormFocus(true);
            }}
            className={formFocus ? 'focused' : ''}
          >
            <Input
              prefixNode={<SearchIcon />}
              name="search"
              placeholder={t('search')}
              value={inputSearchValue}
              onChange={(ev) => setInputSearchValue(ev.target.value)}
              autoComplete="off"
            />

            {(!!brokersRec.length || !!listingsRec.length) && (
              <div className="recomendation-serach">
                {!!listingsRec.length && (
                  <div>
                    <p>{t('listings')}</p>
                    <ul>
                      {listingsRec.map((li) => (
                        <li key={li}>
                          <Button
                            variant="link"
                            onClick={() => {
                              setInputSearchValue(li);
                              setMultipleQueryParams({
                                searchString: li,
                                page: FIRST_PAGE,
                              });
                            }}
                          >
                            {li}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!!brokersRec.length && (
                  <div>
                    <p>{t('brokers')}</p>
                    <ul>
                      {brokersRec.map((br) => (
                        <li key={br}>
                          <Button
                            variant="link"
                            onClick={() => {
                              setInputSearchValue(br);
                              setMultipleQueryParams({
                                searchString: br,
                                page: FIRST_PAGE,
                              });
                            }}
                          >
                            {br}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </div>

      <ReorderResizeTable
        className={classes}
        isDragDisabled
        columns={DashboardMetricColumns}
        data={dealMetrics}
        totalPages={totalPages}
        isDataLoading={isLoading}
        sortingValues={DEAL_SORTING_VALUES}
        customSizes={CUSTOM_COL_SIZES}
        headingTranslations={HeadingTranslations}
        tableContentTransformations={renderedContent}
        onRowClick={handleOnRowClick}
      />

      <PaymentInvoiceSkipped
        ref={bmResponseRef}
        dashboardMetric={dashboardMetric}
        updateDashboardMetric={updateDashboardMetric}
      />
    </div>
  );
};

export default ListingMetrics;
